'use client';

import { useEffect, useState } from 'react';
import { checkRefCode, createAccount, getRegFormFields } from 'actions/auth';
import config from 'config';
import { useSearchParams } from 'next/navigation';

import { Heading, useTheme, VStack } from '@chakra-ui/react';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setRefCodeData } from 'store/reducers/userSlice';

import {
  CreateAccountSteps,
  CreateAccountType,
  FormType,
  GenderFormType
} from 'types';

import { AuthHeader } from 'components/AuthHeader';
import { Text } from 'ui';
import { ConfirmationPasswordForm } from '../../../ConfirmationPasswordForm';
import { AccountForm } from '../AccountForm';
import { Personalization } from '../Personalization';

const initialValues = {
  email: '',
  givname: '',
  surname: '',
  ph_mobile: '',
  bday_date: '',
  postal: '',
  ref_code: '',
  campaign_id: '',
  gender: null,
  policyAccepted: false
};

const personaValues = {
  nickname: '',
  pronoun_id: '',
  gender: null
};

const {
  login: { createAccount: createAccountMocks }
} = config;

interface CreateAccountProps {
  createAccountType?: CreateAccountType;
  referalCode?: string;
  isBackButtonEnabled?: boolean;
}

const CreateAccount = ({
  createAccountType,
  referalCode,
  isBackButtonEnabled = false
}: CreateAccountProps) => {
  const {
    colors: {
      palette: {
        auth: {
          createAccount: { accountForm: palette }
        }
      }
    }
  } = useTheme();
  const dispatch = useAppDispatch();
  const searchParams = useSearchParams();
  const { errorMsg, refCodeData } = useAppSelector((state) => state.user);
  const [step, setStep] = useState<number>(CreateAccountSteps.AccountForm);

  const isReferalFlow =
    Boolean(refCodeData?.is_valid) &&
    createAccountType === CreateAccountType.REFFERAL;
  const isInfluencerFlow =
    Boolean(refCodeData?.is_valid) &&
    createAccountType === CreateAccountType.INFLUENCER;

  const getInitialFormState = () => {
    const emailFromParams = searchParams.get('email') || '';
    const firstNameFromParams = searchParams.get('firstName') || '';
    const lastNameFromParams = searchParams.get('lastName') || '';
    const phoneFromParams = searchParams.get('phone') || '';
    const zipFromParams = searchParams.get('zip') || '';
    if (
      emailFromParams ||
      firstNameFromParams ||
      lastNameFromParams ||
      phoneFromParams ||
      zipFromParams
    ) {
      const formState = {
        ...initialValues,
        email: emailFromParams,
        givname: firstNameFromParams || '',
        surname: lastNameFromParams,
        ph_mobile: phoneFromParams
      };

      if (!isReferalFlow && !isInfluencerFlow) {
        formState.postal = zipFromParams;
      }

      return formState;
    }
    return initialValues;
  };

  const [formState, setFromState] = useState<FormType>(getInitialFormState());
  const [personalizationState, setPersonalizationState] =
    useState<GenderFormType>(personaValues);

  const onAccountSubmit = (values: FormType) => {
    setFromState(values);
    if (isReferalFlow || isInfluencerFlow) {
      setStep(CreateAccountSteps.CreatePassword);
    } else {
      setStep(CreateAccountSteps.Personalization);
    }
  };

  const onStartForm = () => setStep(CreateAccountSteps.AccountForm);

  const onBackFromPassword = () => {
    if (isReferalFlow || isInfluencerFlow) {
      setStep(CreateAccountSteps.AccountForm);
    } else {
      setStep(CreateAccountSteps.Personalization);
    }
  };

  const onSubmitForm = async ({ password }: { password: string }) => {
    dispatch(
      createAccount({
        ...personalizationState,
        ...formState,
        ld_ref_type: refCodeData?.ld_ref_type,
        ld_ref_acct: refCodeData?.ld_ref_acct,
        pass: password
      })
    );
  };

  const onPersonalizationSubmit = async (values: GenderFormType) => {
    setPersonalizationState(values);
    setStep(CreateAccountSteps.CreatePassword);
  };

  useEffect(() => {
    if (errorMsg && step !== CreateAccountSteps.AccountForm) {
      onStartForm();
    }
  }, [errorMsg, step]);

  useEffect(() => {
    if (
      (createAccountType === CreateAccountType.REFFERAL ||
        createAccountType === CreateAccountType.INFLUENCER) &&
      referalCode
    ) {
      dispatch(checkRefCode(referalCode, createAccountType));
    } else {
      dispatch(setRefCodeData(null));
    }
  }, [referalCode, createAccountType, dispatch]);

  useEffect(() => {
    dispatch(getRegFormFields());
  }, [dispatch]);

  const renderForm = () => {
    switch (step) {
      case CreateAccountSteps.AccountForm:
        return (
          <AccountForm
            onSubmit={onAccountSubmit}
            formState={formState}
            referalCode={refCodeData?.is_valid ? referalCode : ''}
            isReferalFlow={isReferalFlow || isInfluencerFlow}
            isBackButtonEnabled={isBackButtonEnabled}
          />
        );

      case CreateAccountSteps.Personalization:
        return (
          <Personalization
            formState={personalizationState}
            onSubmit={onPersonalizationSubmit}
            onBack={onStartForm}
          />
        );

      case CreateAccountSteps.CreatePassword:
        return (
          <ConfirmationPasswordForm
            goBack={onBackFromPassword}
            onSubmit={onSubmitForm}
          />
        );
      default:
        return null;
    }
  };

  const getContent = (stepFlow: CreateAccountSteps) => {
    const defaultContent = {
      title: '',
      subtitle: '',
      mb: { base: '0px', md: '0px' }
    };
    switch (stepFlow) {
      case CreateAccountSteps.AccountForm:
        if (isReferalFlow || isInfluencerFlow) {
          return defaultContent;
        }

        return {
          title: 'Create Account',
          subtitle:
            'Please complete the form below to register and create your account',
          mb: { base: '40px', md: '30px' }
        };

      case CreateAccountSteps.Personalization:
        return {
          title: 'Personalization',
          subtitle:
            'Sharing the optional details below helps us offer a more personalized experience',
          mb: { base: '40px', md: '30px' }
        };

      case CreateAccountSteps.CreatePassword:
        return {
          title: createAccountMocks.title,
          subtitle: createAccountMocks.description,
          mb: { base: '20px', md: '45px' }
        };
      default:
        return defaultContent;
    }
  };
  const { title, subtitle, mb } = getContent(step);

  return (
    <VStack gap={0} w='full' justifyContent='center' alignItems='center'>
      {Boolean(refCodeData?.ref_title) &&
      Boolean(refCodeData?.ref_dscr) &&
      step === CreateAccountSteps.AccountForm ? (
        <VStack gap={0} w='full' mb={{ base: '40px', md: '30px' }}>
          <Text
            mb={{ base: '10px', md: '20px' }}
            textTransform='uppercase'
            textAlign='center'
            fontSize={{ base: 16, md: 20 }}
            color={palette.text.primary}
          >
            {`${refCodeData?.ref_givname} INVITES YOU TO`}
          </Text>
          <Heading
            textStyle='h1'
            as='h1'
            textAlign='center'
            mb={{ base: '10px', md: '20px' }}
            fontWeight='semibold'
            color={palette.text.primary}
            fontSize={[24, 28, 32]}
          >
            {refCodeData?.ref_title}
          </Heading>
          <Text
            textAlign='center'
            fontSize={{ base: 14, md: 18 }}
            color={palette.text.primary}
          >
            {refCodeData?.ref_dscr}
          </Text>
        </VStack>
      ) : (
        <AuthHeader
          mb={mb}
          title={title}
          subtitle={subtitle}
          color={palette.text.primary}
        />
      )}

      {renderForm()}
    </VStack>
  );
};

export default CreateAccount;
